import React from 'react';
import type { DeviceUnitRow, DeviceModelRow } from '../../services/hardwareApi';
import {
  Disclosure, DisclosureBody, StageChip, CoupleHeader, unitLabel,
} from './stageShell';
import ReportProblemButton from './ReportProblemButton';

/**
 * A STAGE WORKED PAIR BY PAIR.
 *
 * Testing and packing are both per-device work whose UNIT is the pair: a test
 * sale needs a till and the reader it is coupled to, and a pair that travels
 * in two different boxes is the thing the coupling lookup exists to prevent.
 * Drawn as one flat list of devices, neither reads as pair work at all.
 *
 * This groups what is already there. It fetches nothing, saves nothing and
 * decides nothing — `children` is the panel that owns the device's checklist,
 * called once per device, and `footer` is whatever the pair needs under it.
 *
 * AN UNPAIRED DEVICE IS NOT DROPPED. A label printer or a cash drawer has no
 * couple and still has to be tested and packed, so anything left over is drawn
 * under its own heading rather than silently disappearing — which is exactly
 * what grouping by pair would otherwise do to it.
 */

export interface CouplePair {
  pos: DeviceUnitRow;
  payment?: DeviceUnitRow;
}

/** Pair the order's devices the way the couple stage recorded them. */
export const pairUnits = (
  units: DeviceUnitRow[],
  posUnitIds: readonly string[],
  paymentUnitIds: readonly string[],
): { pairs: CouplePair[]; loose: DeviceUnitRow[] } => {
  const byId = new Map(units.map((u) => [u.unitId, u]));
  const pairs: CouplePair[] = [];
  const spoken = new Set<string>();

  for (const u of units) {
    if (!posUnitIds.includes(u.unitId)) continue;
    const mate = u.coupledUnitId ? byId.get(u.coupledUnitId) : undefined;
    pairs.push({ pos: u, payment: mate && mate.unitId !== u.unitId ? mate : undefined });
    spoken.add(u.unitId);
    if (mate) spoken.add(mate.unitId);
  }

  /* Everything the pairs did not account for. A payment device that was never
     coupled belongs here too — it is still on the order and still has work. */
  const loose = units.filter((u) => !spoken.has(u.unitId));
  return { pairs, loose };
};

const CoupleChecklistStage: React.FC<{
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  posUnitIds: readonly string[];
  paymentUnitIds: readonly string[];
  /** The panel that owns this device's checklist, drawn inside the pair. */
  children: (unit: DeviceUnitRow) => React.ReactNode;
  /** Anything the PAIR needs under both devices — a box, a test sale. */
  footer?: (pair: CouplePair) => React.ReactNode;
  /** The chip on a pair's header. */
  chip?: (pair: CouplePair) => React.ReactNode;
  /** Said when the order has no devices at all. */
  empty?: string;
  /** Patch one device in place when a problem is reported against it. */
  onUnitChanged?: (unit: DeviceUnitRow) => void;
}> = ({
  units, models, posUnitIds, paymentUnitIds, children, footer, chip, onUnitChanged,
  empty = 'No devices on this order yet.',
}) => {
  const { pairs, loose } = pairUnits(units, posUnitIds, paymentUnitIds);
  const modelOf = (id?: string) => models.find((m) => m.modelId === id);

  if (pairs.length === 0 && loose.length === 0) {
    return <p className="text-[12.5px] text-stone-500">{empty}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {pairs.map((p) => (
        <Disclosure
          key={p.pos.unitId}
          tone={p.payment ? 'done' : 'plain'}
          defaultOpen={pairs.length === 1}
          header={(
            <CoupleHeader
              pos={p.pos}
              posModel={modelOf(p.pos.modelId)}
              payment={p.payment}
              paymentModel={modelOf(p.payment?.modelId)}
              fallback={(
                <span className="text-[11.5px] text-stone-500">
                  Not coupled — this till has no reader recorded against it.
                </span>
              )}
            />
          )}
          chip={chip ? chip(p) : undefined}
          actions={(
            <ReportProblemButton
              units={[p.pos, ...(p.payment ? [p.payment] : [])]}
              onUnitChanged={onUnitChanged}
              subtle
            />
          )}
        >
          <DisclosureBody className="space-y-2">
            {children(p.pos)}
            {p.payment && children(p.payment)}
            {footer?.(p)}
          </DisclosureBody>
        </Disclosure>
      ))}

      {loose.length > 0 && (
        <div className="space-y-2">
          <p className="pt-1 text-[10px] font-bold uppercase tracking-[0.08em] text-stone-400">
            Not part of a pair
          </p>
          {loose.map((u) => (
            <Disclosure
              key={u.unitId}
              header={(
                <span className="flex min-w-0 items-center gap-2.5">
                  <span className="min-w-0">
                    <span className="block truncate text-[12px] font-semibold text-stone-800">
                      {modelOf(u.modelId)?.modelName || u.modelId}
                    </span>
                    <span className="block font-mono text-[10px] font-semibold tracking-[0.07em] text-stone-500">
                      {unitLabel(u)}
                    </span>
                  </span>
                </span>
              )}
              chip={<StageChip tone="idle">On its own</StageChip>}
              actions={<ReportProblemButton units={[u]} onUnitChanged={onUnitChanged} subtle />}
            >
              <DisclosureBody>{children(u)}</DisclosureBody>
            </Disclosure>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoupleChecklistStage;
